import React, { useState, useEffect } from 'react';
import { useSearchLog } from '../../utils/telemetry';
import { ArrowLeft, Search, ChevronRight, FileText, Users, GraduationCap, AlertCircle, Building2, Microscope, Video, Presentation, Stethoscope } from 'lucide-react';
import { buildSearchIndex, createSearchEngine, performSearch } from '../../utils/searchUtils';
import { trackSearch } from '../../utils/analyticsUtils';

const SearchResultsScreen = ({ onBack, hospitals, initialQuery = '', onNavigate }) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchIndex, setSearchIndex] = useState([]);
  const [searchEngine, setSearchEngine] = useState(null);
  const [, setIndexLoading] = useState(true);

  // Build search index asynchronously
  useEffect(() => {
    const loadSearchIndex = async () => {
      setIndexLoading(true);
      const index = await buildSearchIndex(hospitals);
      setSearchIndex(index);
      setSearchEngine(createSearchEngine(index));
      setIndexLoading(false);
    };

    loadSearchIndex();
  }, [hospitals]);

  // Debounced search effect
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2 || !searchEngine) {
      setSearchResults([]);
      setSuggestions([]);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(() => {
      const { results, suggestions: newSuggestions } = performSearch(
        searchEngine,
        searchQuery,
        searchIndex
      );
      setSearchResults(results);
      setSuggestions(newSuggestions);
      setIsSearching(false);

      // Track the search query
      trackSearch(searchQuery, results.length);
    }, 400); // 400ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchEngine, searchIndex]);

  // Get icon for result type
  const getResultIcon = (type, metadata = {}) => {
    switch (type) {
      case 'protocol':
        return <AlertCircle size={20} className="text-red-600" />;
      case 'contact':
        return <Users size={20} className="text-blue-600" />;
      case 'education':
        return <GraduationCap size={20} className="text-emerald-600" />;
      case 'education-content':
        // Different icon based on file type
        if (metadata.fileType === 'video') {
          return <Video size={20} className="text-emerald-600" />;
        } else if (metadata.fileType === 'presentation') {
          return <Presentation size={20} className="text-emerald-600" />;
        } else {
          return <FileText size={20} className="text-emerald-600" />;
        }
      case 'hospital':
        return <Building2 size={20} className="text-slate-600" />;
      case 'hospital-content':
        return <Building2 size={20} className="text-slate-600" />;
      case 'section':
        return <FileText size={20} className="text-purple-600" />;
      case 'research-content':
        return <Microscope size={20} className="text-teal-600" />;
      case 'bcm-content':
        return <Building2 size={20} className="text-sky-600" />;
      case 'clinical-tool':
        return <Stethoscope size={20} className="text-purple-600" />;
      default:
        return <FileText size={20} className="text-gray-600" />;
    }
  };

  // Handle result click
  const { markPicked } = useSearchLog('app', searchQuery, isSearching ? null : searchResults.length);
  const handleResultClick = (result) => {
    markPicked(result.id || result.title || result.type);
    if (result.type === 'protocol' && result.navigationPath?.protocol) {
      onNavigate('emergency', result.navigationPath.protocol);
    } else if (result.type === 'hospital' && result.navigationPath?.hospital) {
      onNavigate('hospital', result.navigationPath.hospital);
    } else if (result.type === 'hospital-content' && result.navigationPath?.hospital) {
      onNavigate('hospital', result.navigationPath.hospital, result.navigationPath.action);
    } else if (result.type === 'section' && result.navigationPath?.screen) {
      onNavigate(result.navigationPath.screen, null);
    } else if (result.type === 'education') {
      onNavigate('education', null);
    } else if (result.type === 'education-content' && result.navigationPath) {
      // Navigate to education with specific content selected
      onNavigate('education', result.navigationPath);
    } else if (result.type === 'research-content' && result.navigationPath?.screen) {
      onNavigate(result.navigationPath.screen, null);
    } else if (result.type === 'bcm-content' && result.navigationPath?.screen) {
      onNavigate(result.navigationPath.screen, null);
    } else if (result.type === 'clinical-tool' && result.navigationPath?.screen) {
      onNavigate(result.navigationPath.screen, null);
    }
  };

  // Truncate content for snippet
  const getSnippet = (content, maxLength = 150) => {
    if (!content || content.length === 0) return '';
    const text = typeof content === 'string' ? content : JSON.stringify(content);
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header with Search */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-6 pb-8 sticky top-0 z-20">
        <button onClick={() => { onBack(); }} className="flex items-center gap-2 text-white mb-4 hover:opacity-80">
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>

        {/* Search Bar */}
        <div className="bg-slate-50 dark:bg-gray-800 rounded-xl shadow-lg border-2 border-slate-300 dark:border-gray-700 focus-within:border-blue-400 dark:focus-within:border-cyan-400 transition-colors">
          <div className="relative">
            <Search className="absolute left-4 top-4 text-gray-500 dark:text-gray-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for anything"
              className="w-full pl-12 pr-4 py-4 rounded-xl focus:outline-none text-gray-800 dark:text-white bg-transparent placeholder-gray-400 dark:placeholder-gray-500"
              autoFocus
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="px-6 py-6">
        {/* Loading State */}
        {isSearching && (
          <div className="text-center py-8">
            <p className="text-gray-600">Searching...</p>
          </div>
        )}

        {/* Empty State (no query) */}
        {!searchQuery && !isSearching && (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <Search size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600 mb-4">Enter a search term to find protocols, contacts & more</p>
            <div className="text-left mt-6">
              <p className="text-sm font-semibold text-gray-700 mb-2">Try searching for:</p>
              <div className="space-y-2">
                {['MH', 'LAST', 'Dantrolene', 'Contact information'].map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSearchQuery(example)}
                    className="block w-full text-left px-4 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-cyan-400 transition-colors text-sm dark:text-gray-300"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* No Results State */}
        {searchQuery && !isSearching && searchResults.length === 0 && (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <AlertCircle size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-800 font-semibold mb-2">No results found for "{searchQuery}"</p>
            {suggestions.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Did you mean:</p>
                <div className="space-y-2">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSearchQuery(suggestion)}
                      className="block w-full px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-cyan-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-sm font-medium"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results List */}
        {!isSearching && searchResults.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 mb-4">
              Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>

            {searchResults.map((result, idx) => (
              <button
                key={idx}
                onClick={() => handleResultClick(result)}
                className="w-full bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 hover:shadow-lg transition-all text-left border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-cyan-400"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {getResultIcon(result.type, result.metadata)}
                  </div>
                  <div className="flex-1 min-w-0">
                    {/* Breadcrumbs */}
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {result.breadcrumbs.map((crumb, i) => (
                        <React.Fragment key={i}>
                          <span>{crumb}</span>
                          {i < result.breadcrumbs.length - 1 && (
                            <ChevronRight size={12} className="flex-shrink-0" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    {/* Title */}
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{result.title}</h3>

                    {/* Snippet */}
                    {result.content && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                        {getSnippet(result.content)}
                      </p>
                    )}
                  </div>
                  <ChevronRight size={20} className="flex-shrink-0 text-gray-400 dark:text-gray-500 mt-1" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsScreen;
